import { useState, useEffect, useCallback, useMemo } from "react"
import { Link } from "react-router-dom"
import { Search, Plus, ChevronDown, Grid3x3, List, User, Bell, HelpCircle, Share2, RefreshCw, Download, Trash2, FolderInput, X, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from "lucide-react"
import { DashboardSidebar } from "@/components/dashboard-sidebar"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { CloudFileGrid } from "@/components/cloud-file-grid"
import { CloudFileList } from "@/components/cloud-file-list"
import { CloudUploadDialog } from "@/components/cloud-upload-dialog"
import { NAVIGATION_ROUTES } from '@/constants/routes'
import { getCloudFiles, deleteCloudFile, type CloudFileItem } from '@/api/cloud'
import { copyToClipboard } from '@/utils/clipboard'
import { useToast } from "@/hooks/use-toast"

type ViewMode = 'grid' | 'list'
type SortOrder = 'asc' | 'desc' | null
type FileTypeFilter = 'all' | 'image' | 'document' | 'other'

export default function Cloud() {
  const [selectedFiles, setSelectedFiles] = useState<number[]>([])
  const [selectAll, setSelectAll] = useState(false)
  const [files, setFiles] = useState<CloudFileItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [fileTypeFilter, setFileTypeFilter] = useState<FileTypeFilter>('all')
  const [sortOrder, setSortOrder] = useState<SortOrder>(null)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    return (localStorage.getItem('cloud_view_mode') as ViewMode) || 'grid'
  })
  const [showUploadDialog, setShowUploadDialog] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [fileToDelete, setFileToDelete] = useState<CloudFileItem | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const { toast } = useToast()
  const pageSize = 20

  const fetchFiles = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getCloudFiles(page, pageSize)
      setFiles(response.items)
      setTotal(response.total || response.items.length)
      setTotalPages(response.totalPages || 1)
      setSelectedFiles([])
      setSelectAll(false)
    } catch (err) {
      const message = (err as Error).message || "加载失败"
      setError(message)
      toast({
        title: "获取云盘数据失败",
        description: message,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, toast])

  useEffect(() => {
    fetchFiles()
  }, [fetchFiles])

  // 过滤和排序文件
  const filteredAndSortedFiles = useMemo(() => {
    let result = [...files]

    // 搜索过滤
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim()
      result = result.filter((file) => {
        const fileName = (file.displayName || file.originalName).toLowerCase()
        return fileName.includes(query)
      })
    }

    // 格式过滤
    if (fileTypeFilter !== 'all') {
      result = result.filter((file) => {
        if (fileTypeFilter === 'image') {
          // 图片类型：fileType === 'image' 或 contentType 以 image/ 开头
          return file.fileType === 'image' || file.contentType?.startsWith('image/')
        } else if (fileTypeFilter === 'document') {
          // 文档类型：pdf、document、sheet 或 contentType 是 text/ 或 application/pdf
          return file.fileType === 'pdf' || 
                 file.fileType === 'document' || 
                 file.fileType === 'sheet' ||
                 file.contentType === 'application/pdf' ||
                 file.contentType?.startsWith('text/') ||
                 file.contentType?.startsWith('application/vnd.ms-') ||
                 file.contentType?.startsWith('application/vnd.openxmlformats-')
        } else if (fileTypeFilter === 'other') {
          // 其他类型：既不是图片也不是文档（如 video、audio、archive、file 等）
          const isImage = file.fileType === 'image' || file.contentType?.startsWith('image/')
          const isDocument = file.fileType === 'pdf' || 
                            file.fileType === 'document' || 
                            file.fileType === 'sheet' ||
                            file.contentType === 'application/pdf' ||
                            file.contentType?.startsWith('text/') ||
                            file.contentType?.startsWith('application/vnd.ms-') ||
                            file.contentType?.startsWith('application/vnd.openxmlformats-')
          return !isImage && !isDocument
        }
        return true
      })
    }

    // 创建时间排序
    if (sortOrder) {
      result.sort((a, b) => {
        const timeA = new Date(a.createdAt).getTime()
        const timeB = new Date(b.createdAt).getTime()
        if (sortOrder === 'asc') {
          return timeA - timeB // 升序：早的在前
        } else {
          return timeB - timeA // 降序：晚的在前
        }
      })
    }

    return result
  }, [files, searchQuery, fileTypeFilter, sortOrder])

  const handleSelectAll = (checked: boolean) => {
    if (filteredAndSortedFiles.length === 0) {
      setSelectedFiles([])
      setSelectAll(false)
      return
    }
    setSelectAll(checked)
    if (checked) {
      setSelectedFiles(filteredAndSortedFiles.map((file) => file.id))
    } else {
      setSelectedFiles([])
    }
  }

  useEffect(() => {
    const totalCount = filteredAndSortedFiles.length
    if (totalCount === 0) {
      setSelectAll(false)
      return
    }
    // 检查当前显示的文件是否全部选中
    const allSelected = filteredAndSortedFiles.every((file) => selectedFiles.includes(file.id))
    setSelectAll(allSelected)
  }, [selectedFiles, filteredAndSortedFiles])

  // 分享功能：复制下载链接
  const handleShare = async () => {
    if (selectedFiles.length === 0) {
      toast({
        title: "请选择要分享的文件",
        variant: "destructive",
      })
      return
    }

    const selectedFileItems = filteredAndSortedFiles.filter((file) => selectedFiles.includes(file.id))
    if (selectedFileItems.length === 0) {
      return
    }

    // 如果只选了一个文件，复制该文件的下载链接
    if (selectedFileItems.length === 1) {
      const file = selectedFileItems[0]
      
      // 检查 downloadUrl 是否为空或无效
      if (!file.downloadUrl || file.downloadUrl.trim() === '' || file.downloadUrl === '/') {
        toast({
          title: "无法分享",
          description: "该文件的下载链接无效（shareKey 或 fileId 为空）",
          variant: "destructive",
        })
        return
      }
      
      // 确保 URL 包含 /api 前缀
      let downloadUrl = file.downloadUrl
      if (!downloadUrl.startsWith('http')) {
        if (downloadUrl.startsWith('/download/')) {
          downloadUrl = `/api${downloadUrl}`
        }
        downloadUrl = `${window.location.origin}${downloadUrl}`
      }
      
      const success = await copyToClipboard(downloadUrl)
      if (success) {
        toast({
          title: "链接已复制",
          description: "分享链接已复制到剪贴板",
        })
      } else {
        toast({
          title: "复制失败",
          description: "请手动复制链接",
          variant: "destructive",
        })
      }
    } else {
      // 多个文件：复制分享的下载链接（整个分享的链接）
      // 取第一个文件的分享链接作为分享链接
      const firstFile = selectedFileItems[0]
      const shareUrl = `${window.location.origin}/download/group/${firstFile.shareKey}`
      const success = await copyToClipboard(shareUrl)
      if (success) {
        toast({
          title: "分享链接已复制",
          description: "多个文件的分享链接已复制到剪贴板",
        })
      } else {
        toast({
          title: "复制失败",
          description: "请手动复制链接",
          variant: "destructive",
        })
      }
    }
  }

  // 下载功能：下载文件
  const handleDownload = async () => {
    if (selectedFiles.length === 0) {
      toast({
        title: "请选择要下载的文件",
        variant: "destructive",
      })
      return
    }

    const selectedFileItems = filteredAndSortedFiles.filter((file) => selectedFiles.includes(file.id))
    
    setLoading(true)
    try {
      // 如果只选了一个文件，下载该文件
      if (selectedFileItems.length === 1) {
        const file = selectedFileItems[0]
        const token = localStorage.getItem('accessToken')
        
        // 确保 URL 包含 /api 前缀（后端路由是 /api/download/group/:shareKey）
        let downloadUrl = file.downloadUrl
        if (!downloadUrl.startsWith('http')) {
          // 如果 downloadUrl 是 /download/group/xxx，需要改为 /api/download/group/xxx
          if (downloadUrl.startsWith('/download/')) {
            downloadUrl = `/api${downloadUrl}`
          }
          downloadUrl = `${window.location.origin}${downloadUrl}`
        }
        // 使用原生链接触发流式下载（避免大文件在浏览器内存分配 Blob 导致断流）
        const downloadUrlWithToken = token 
          ? (downloadUrl.includes('?') ? `${downloadUrl}&token=${encodeURIComponent(token)}` : `${downloadUrl}?token=${encodeURIComponent(token)}`)
          : downloadUrl

        const fileName = file.displayName || file.originalName || 'download'
        const a = document.createElement('a')
        a.href = downloadUrlWithToken
        a.download = fileName
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)

        toast({
          title: "下载开始",
          description: `正在下载 ${fileName}`,
        })
      } else {
        // 多个文件：检查是否来自同一个分享
        const firstFile = selectedFileItems[0]
        const allSameShare = selectedFileItems.every(file => file.shareKey === firstFile.shareKey)
        
        if (allSameShare) {
          // 如果所有文件来自同一个分享，只下载选中的文件
          const token = localStorage.getItem('accessToken')
          
          // 构建包含多个 fileId 的 URL（fileId=xxx&fileId=yyy）
          const fileIdParams = selectedFileItems
            .map(file => `fileId=${encodeURIComponent(file.fileId)}`)
            .join('&')
          let shareUrl = `${window.location.origin}/api/download/group/${firstFile.shareKey}?${fileIdParams}`
          if (token) {
            shareUrl += `&token=${encodeURIComponent(token)}`
          }

          const a = document.createElement('a')
          a.href = shareUrl
          a.download = `${firstFile.shareName || 'files'}.zip`
          a.style.display = 'none'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)

          toast({
            title: "下载开始",
            description: `正在下载选中的 ${selectedFileItems.length} 个文件`,
          })
        } else {
          // 如果文件来自不同的分享，按分享组排队自动依次下载，无需报错阻断
          toast({
            title: "开始排队下载",
            description: `选中的文件来自不同分享，正在依次自动启动 ${selectedFileItems.length} 个下载任务...`,
          })

          const groups = new Map<string, typeof selectedFileItems>()
          selectedFileItems.forEach((item) => {
            const list = groups.get(item.shareKey) || []
            list.push(item)
            groups.set(item.shareKey, list)
          })

          let delay = 0
          for (const [shareKey, items] of groups.entries()) {
            setTimeout(() => {
              const token = localStorage.getItem('accessToken')
              if (items.length === 1) {
                let dlUrl = items[0].downloadUrl
                if (!dlUrl.startsWith('http')) {
                  if (dlUrl.startsWith('/download/')) {
                    dlUrl = `/api${dlUrl}`
                  }
                  dlUrl = `${window.location.origin}${dlUrl}`
                }
                const finalUrl = token
                  ? dlUrl.includes('?')
                    ? `${dlUrl}&token=${encodeURIComponent(token)}`
                    : `${dlUrl}?token=${encodeURIComponent(token)}`
                  : dlUrl

                const a = document.createElement('a')
                a.href = finalUrl
                a.download = items[0].displayName || items[0].originalName || 'download'
                a.style.display = 'none'
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
              } else {
                const fileIdParams = items
                  .map((file) => `fileId=${encodeURIComponent(file.fileId)}`)
                  .join('&')
                let shareUrl = `${window.location.origin}/api/download/group/${shareKey}?${fileIdParams}`
                if (token) {
                  shareUrl += `&token=${encodeURIComponent(token)}`
                }
                const a = document.createElement('a')
                a.href = shareUrl
                a.download = `${items[0].shareName || 'files'}.zip`
                a.style.display = 'none'
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
              }
            }, delay)
            delay += 600
          }
        }
      }
    } catch (err) {
      const message = (err as Error).message || "下载失败"
      toast({
        title: "下载失败",
        description: message,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  // 打开批量删除确认对话框
  const handleOpenBatchDelete = () => {
    if (selectedFiles.length === 0) {
      toast({
        title: "请选择要删除的文件",
        variant: "destructive",
      })
      return
    }
    setFileToDelete(null)
    setDeleteDialogOpen(true)
  }

  // 打开单个文件删除确认对话框
  const handleSingleFileDelete = useCallback((file: CloudFileItem) => {
    setFileToDelete(file)
    setDeleteDialogOpen(true)
  }, [])

  // 执行最终删除确认
  const handleConfirmDelete = async () => {
    setLoading(true)
    try {
      if (fileToDelete) {
        // 单个删除
        await deleteCloudFile(String(fileToDelete.shareId), fileToDelete.fileId)
        toast({
          title: "删除成功",
          description: `已删除 ${fileToDelete.displayName || fileToDelete.originalName}`,
        })
      } else {
        // 批量删除
        const selectedFileItems = filteredAndSortedFiles.filter((file) => selectedFiles.includes(file.id))
        const deletePromises = selectedFileItems.map((file) =>
          deleteCloudFile(String(file.shareId), file.fileId)
        )
        await Promise.all(deletePromises)
        toast({
          title: "删除成功",
          description: `已删除 ${selectedFileItems.length} 个文件`,
        })
      }
      await fetchFiles()
    } catch (err) {
      const message = (err as Error).message || "删除失败"
      toast({
        title: "删除失败",
        description: message,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
      setDeleteDialogOpen(false)
      setFileToDelete(null)
    }
  }

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode)
    localStorage.setItem('cloud_view_mode', mode)
  }

  // 单个文件分享处理
  const handleSingleFileShare = useCallback(async (file: CloudFileItem) => {
    // 检查 downloadUrl 是否为空或无效
    if (!file.downloadUrl || file.downloadUrl.trim() === '' || file.downloadUrl === '/') {
      toast({
        title: "无法分享",
        description: "该文件的下载链接无效（shareKey 或 fileId 为空）",
        variant: "destructive",
      })
      return
    }
    
    // 确保 URL 包含 /api 前缀
    let downloadUrl = file.downloadUrl
    if (!downloadUrl.startsWith('http')) {
      if (downloadUrl.startsWith('/download/')) {
        downloadUrl = `/api${downloadUrl}`
      }
      downloadUrl = `${window.location.origin}${downloadUrl}`
    }
    
    const success = await copyToClipboard(downloadUrl)
    if (success) {
      toast({
        title: "链接已复制",
        description: "分享链接已复制到剪贴板",
      })
    } else {
      toast({
        title: "复制失败",
        description: "请手动复制链接",
        variant: "destructive",
      })
    }
  }, [toast])
  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* 左侧边栏 */}
      <DashboardSidebar activePage="cloud" />

      {/* 右侧内容区 */}
      <div className="flex-1 flex flex-col">
        {/* 顶部标题栏 */}
        <div className="bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">
            {/* 左侧：标题 */}
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-gray-900">我的云盘</h1>
            </div>

            {/* 中间搜索框 */}
            <div className="flex-1 max-w-md mx-8">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  placeholder="在我的云盘内搜索" 
                  className="pl-10 bg-gray-50 border-gray-200 rounded-lg"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* 右侧操作按钮 */}
            <div className="flex items-center gap-3">
              <Button 
                className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 rounded-lg h-9 px-4 text-sm"
                onClick={() => setShowUploadDialog(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                新建
              </Button>
              <Button variant="ghost" size="sm" className="text-gray-700 hover:text-gray-900 h-auto p-0 text-sm">
                升级会员
              </Button>
              <Button variant="ghost" size="icon" className="text-gray-600 hover:text-gray-900 h-8 w-8">
                <HelpCircle className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="text-gray-600 hover:text-gray-900 relative h-8 w-8">
                <Bell className="w-4 h-4" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              </Button>
              <Link to={NAVIGATION_ROUTES.SETTINGS}>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-gray-600 hover:text-gray-900 h-8 w-8"
                >
                  <User className="w-4 h-4" />
                </Button>
              </Link>
              <Link to={NAVIGATION_ROUTES.HOME}>
                <Button variant="ghost" size="icon" className="text-gray-600 hover:text-gray-900 h-8 w-8">
                  <X className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* 次级工具栏 */}
        <div className="bg-white border-b border-gray-200 px-8 py-3">
          <div className="flex items-center justify-between">
            {/* 左侧：全选 + 计数 */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <Checkbox 
                  checked={selectAll}
                  onCheckedChange={(checked) => handleSelectAll(Boolean(checked))}
                  disabled={loading || files.length === 0}
                />
                <span>全选</span>
              </label>
              <span className="text-sm text-gray-600">
                {selectedFiles.length > 0 
                  ? `已选 ${selectedFiles.length} 项` 
                  : `共 ${filteredAndSortedFiles.length} 项${searchQuery || fileTypeFilter !== 'all' ? `（过滤后）` : ''}`}
              </span>
            </div>

            {/* 右侧：排序和视图切换 */}
            <div className="flex items-center gap-3">
              {/* 格式过滤 */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-gray-600 hover:text-gray-900 h-8">
                    <span className="text-sm">格式</span>
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem 
                    onClick={() => setFileTypeFilter('all')}
                    className={fileTypeFilter === 'all' ? 'bg-gray-100' : ''}
                  >
                    全部格式
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => setFileTypeFilter('image')}
                    className={fileTypeFilter === 'image' ? 'bg-gray-100' : ''}
                  >
                    图片
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setFileTypeFilter('document')}
                    className={fileTypeFilter === 'document' ? 'bg-gray-100' : ''}
                  >
                    文档
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setFileTypeFilter('other')}
                    className={fileTypeFilter === 'other' ? 'bg-gray-100' : ''}
                  >
                    其他
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* 创建时间排序 */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-gray-600 hover:text-gray-900 h-8">
                    <span className="text-sm">创建时间</span>
                    {sortOrder === 'asc' ? (
                      <ArrowUp className="w-4 h-4 ml-1" />
                    ) : sortOrder === 'desc' ? (
                      <ArrowDown className="w-4 h-4 ml-1" />
                    ) : (
                      <ArrowUpDown className="w-4 h-4 ml-1" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem 
                    onClick={() => setSortOrder(null)}
                    className={sortOrder === null ? 'bg-gray-100' : ''}
                  >
                    默认
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => setSortOrder('desc')}
                    className={sortOrder === 'desc' ? 'bg-gray-100' : ''}
                  >
                    <ArrowDown className="w-4 h-4 mr-2" />
                    从新到旧
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setSortOrder('asc')}
                    className={sortOrder === 'asc' ? 'bg-gray-100' : ''}
                  >
                    <ArrowUp className="w-4 h-4 mr-2" />
                    从旧到新
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* 视图切换 */}
              <div className="flex items-center gap-0.5 border border-gray-200 rounded-lg p-0.5">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className={`h-7 w-7 ${viewMode === 'grid' ? 'text-gray-900 bg-gray-100' : 'text-gray-400'}`}
                  onClick={() => handleViewModeChange('grid')}
                  title="网格视图"
                >
                  <Grid3x3 className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className={`h-7 w-7 ${viewMode === 'list' ? 'text-gray-900 bg-gray-100' : 'text-gray-400'}`}
                  onClick={() => handleViewModeChange('list')}
                  title="列表视图"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* 主内容区 */}
        <main className="flex-1 p-8 bg-gray-50 relative">
          <div className="max-w-7xl mx-auto space-y-4">
            {error && !loading && (
              <div className="rounded-lg border border-red-200 bg-red-50 text-red-600 px-4 py-2 text-sm">
                {error}
              </div>
            )}
            {viewMode === 'grid' ? (
              <CloudFileGrid 
                files={filteredAndSortedFiles}
                selectedFiles={selectedFiles}
                onSelectChange={setSelectedFiles}
                loading={loading}
              />
            ) : (
              <CloudFileList 
                files={filteredAndSortedFiles}
                selectedFiles={selectedFiles}
                onSelectChange={setSelectedFiles}
                onShare={handleSingleFileShare}
                onDelete={handleSingleFileDelete}
                loading={loading}
              />
            )}

            {/* 分页组件 */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white rounded-lg">
                <div className="text-sm text-gray-600">
                  共 {total} 项，第 {page} / {totalPages} 页
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (page > 1) {
                        setPage(page - 1)
                      }
                    }}
                    disabled={page === 1 || loading}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    上一页
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number
                      if (totalPages <= 5) {
                        pageNum = i + 1
                      } else if (page <= 3) {
                        pageNum = i + 1
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i
                      } else {
                        pageNum = page - 2 + i
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={page === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setPage(pageNum)}
                          disabled={loading}
                          className="w-9"
                        >
                          {pageNum}
                        </Button>
                      )
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (page < totalPages) {
                        setPage(page + 1)
                      }
                    }}
                    disabled={page === totalPages || loading}
                  >
                    下一页
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
            {totalPages <= 1 && total > 0 && (
              <div className="text-center text-sm text-gray-500 py-4">
                共 {total} 项
              </div>
            )}
          </div>

          {/* 底部浮动工具栏 */}
          {selectedFiles.length > 0 && (
            <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50">
              <div className="bg-gray-900 text-white rounded-full shadow-2xl px-6 py-3 flex items-center gap-6">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-white hover:text-white hover:bg-white/20 h-9 w-9"
                  title="分享"
                  onClick={handleShare}
                  disabled={loading}
                >
                  <Share2 className="w-5 h-5" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-white hover:text-white hover:bg-white/20 h-9 w-9"
                  title="刷新"
                  onClick={() => fetchFiles()}
                  disabled={loading}
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-white hover:text-white hover:bg-white/20 h-9 w-9"
                  title="下载"
                  onClick={handleDownload}
                  disabled={loading}
                >
                  <Download className="w-5 h-5" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-white hover:text-white hover:bg-white/20 h-9 w-9"
                  title="删除"
                  onClick={handleOpenBatchDelete}
                  disabled={loading}
                >
                  <Trash2 className="w-5 h-5" />
                </Button>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-white/40 hover:text-white/40 h-9 w-9 cursor-not-allowed"
                          title="移动"
                          disabled
                        >
                          <FolderInput className="w-5 h-5" />
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="bg-gray-800 text-white text-xs border-0">
                      当前版本暂未开放自定义目录移动
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          )}
        </main>

        {/* 上传对话框 */}
        <CloudUploadDialog
          open={showUploadDialog}
          onClose={() => setShowUploadDialog(false)}
          onUploadComplete={() => {
            // 上传完成后刷新文件列表
            fetchFiles()
          }}
        />

        {/* 规范删除确认对话框 */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>确认删除文件？</AlertDialogTitle>
              <AlertDialogDescription>
                {fileToDelete
                  ? `确定要删除文件 "${fileToDelete.displayName || fileToDelete.originalName}" 吗？删除后文件将被移至回收站。`
                  : `确定要删除选中的 ${selectedFiles.length} 个文件吗？删除后文件将被移至回收站。`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={loading}>取消</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {loading ? "删除中..." : "确认删除"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
